import { ICourseEnrollmentItem } from '../components/SelectAdditionalCourses/components/SelectCourseCard/SelectCourseCard'

const getSafeNumber = (value: unknown): number | null => {
  const numericValue = Number(value)
  return Number.isFinite(numericValue) ? numericValue : null
}

export const getCustomTariffPrice = (
  tariff: ICourseTariff,
  value: number,
  unitPrice = Number(tariff?.first_price),
): number => {
  const basePrice = Math.max(0, unitPrice) * value
  if (!tariff?.is_custom_tariff_values) return basePrice

  const item = tariff?.custom_tariff_values?.find(
    (customValue) => Number(customValue.value) === Number(value),
  )
  if (!item) return basePrice

  const discountValue = Math.max(0, Number(item.discount_value ?? 0))
  const discount =
    item.discount_type === 'percent'
      ? (basePrice * Math.min(100, discountValue)) / 100
      : discountValue

  return Number(Math.max(0, basePrice - discount).toFixed(2))
}

export const getCustomTariffBasePrice = (
  tariff?: ICourseTariff,
  value?: number,
): number | null => {
  if (
    !tariff ||
    !value ||
    !['hour', 'minute', 'group_per_lesson'].includes(tariff.type) ||
    !tariff.is_custom_tariff_values
  ) {
    return null
  }

  const basePrice = Math.max(0, Number(tariff.first_price)) * value
  const price = getCustomTariffPrice(tariff, value)

  return basePrice > price ? Number(basePrice.toFixed(2)) : null
}

export const getTariffPrice = (
  tariff?: ICourseTariff,
  value?: number,
): number => {
  if (!tariff || !value) return 0
  try {
    if (['hour', 'minute', 'group_per_lesson']?.includes(tariff?.type)) {
      return getCustomTariffPrice(tariff, value)
    }
    if (['month']?.includes(tariff?.type)) {
      const basePrice = Number(tariff?.first_price) * value
      if (
        tariff?.catalog_to_first_day_of_month ||
        tariff?.renew_to_first_day_of_month
      ) {
        const dayOfMonth = new Date().getDate()
        const paidDays = dayOfMonth === 1 ? 30 : Math.max(1, 30 - dayOfMonth)
        const firstMonthPrice = (Number(tariff?.first_price) * paidDays) / 30
        return Number(
          (firstMonthPrice + Number(tariff?.first_price) * (value - 1)).toFixed(
            2,
          ),
        )
      }
      return basePrice
    }
    if ('stream_custom_period' === tariff?.type) {
      const price = getSafeNumber(
        tariff?.tariffPeriods?.find((item) => item.months === value)?.price,
      )
      return price ?? Number(tariff?.first_price)
    }
    if ('stream_custom_days' === tariff?.type) {
      const price = getSafeNumber(
        tariff?.tariffDays?.find((item) => item.days === value)?.price,
      )
      return price ?? Number(tariff?.first_price)
    }
    if ('stream_custom_weeks' === tariff?.type) {
      const price = getSafeNumber(
        tariff?.tariffWeeks?.find((item) => item.weeks === value)?.price,
      )
      return price ?? Number(tariff?.first_price)
    }
    return Number(tariff?.first_price)
  } catch (error) {
    return Number(tariff?.first_price)
  }
}

export const getPromoCodeDiscountString = (
  discount?: IPromoCodeDiscount | null,
): string => {
  if (!discount) return ''
  switch (discount?.type) {
    case 'percent':
      return `- ${Number(discount.discount)}%`
    case 'sum':
      return `- ${Number(discount.discount)}р`
  }
  return 'с промокодом'
}

export const getPriceWithDiscount = (
  price: number,
  discount?: IPromoCodeDiscount | null,
): number => {
  try {
    let priceWithDiscount = price
    if (discount) {
      switch (discount?.type) {
        case 'percent':
          priceWithDiscount = price - (price * Number(discount.discount)) / 100
          break
        case 'sum':
          priceWithDiscount = price - Number(discount.discount)
          break

        default:
          priceWithDiscount = price
      }
    } else {
      priceWithDiscount = price
    }
    return Number((priceWithDiscount < 0 ? 0 : priceWithDiscount).toFixed(2))
  } catch (error) {
    return price
  }
}

export const getTariffPriceWithDiscount = (
  tariff?: ICourseTariff,
  value?: number,
  discount?: IPromoCodeDiscount | null,
): number => {
  if (!tariff || !value || tariff?.type === 'free') return 0
  try {
    const currentPrice = getTariffPrice(tariff, value)
    let price = getTariffPrice(tariff, value)
    if (discount) {
      switch (discount?.type) {
        case 'percent':
          price =
            currentPrice - (currentPrice * Number(discount.discount)) / 100
          break
        case 'sum':
          price = currentPrice - Number(discount.discount)
          break

        default:
          price = currentPrice
      }
    } else {
      price = currentPrice
    }
    return Number((price < 0 ? 0 : price).toFixed(2))
  } catch (error) {
    return getTariffPrice(tariff, value)
  }
}

export const getTariffOldPrice = (
  tariff?: ICourseTariff,
  value?: number,
): string => {
  if (!tariff || !value) return ''
    try {
      if (
        tariff?.isCheckboxesView &&
        'stream_custom_period' === tariff?.type
      ) {
        const oldPrice = getSafeNumber(
          tariff?.tariffPeriods?.find((item) => item?.months === value)
            ?.old_price,
        )

        if (oldPrice !== null) {
          return oldPrice.toString()
        }
      }

    if (tariff?.isCheckboxesView && 'stream_custom_days' === tariff?.type) {
      const oldPrice = getSafeNumber(
        tariff?.tariffDays?.find((item) => item?.days === value)?.old_price,
      )

      if (oldPrice !== null) {
        return oldPrice.toString()
      }
    }

    if (tariff?.isCheckboxesView && 'stream_custom_weeks' === tariff?.type) {
      const oldPrice = getSafeNumber(
        tariff?.tariffWeeks?.find((item) => item?.weeks === value)?.old_price,
      )

      if (oldPrice !== null) {
        return oldPrice.toString()
      }
    }

    if (tariff?.old_price === null || tariff?.old_price === '') return ''

    let oldPrice = Number(tariff?.old_price)

    if (
      ['minute', 'hour', 'group_per_lesson', 'month']?.includes(tariff?.type)
    ) {
      oldPrice = oldPrice * value
    }

    return Number(oldPrice.toFixed(2))?.toString()
  } catch (error) {
    return ''
  }
}

export const getTotalPrice = (
  tariff?: ICourseTariff,
  value?: number,
  discount?: IPromoCodeDiscount | null,
  withDemo?: boolean,
  extraCourses?: ICourseEnrollmentItem[],
  extraCoursesDiscount?: number[],
  extraCoursesDiscountUnit?: string,
): number => {
  try {
    const firstFree = tariff?.type === 'free' || (withDemo ?? false)
    let coursePrice = withDemo
      ? 0
      : getTariffPriceWithDiscount(tariff, value, discount)
    extraCourses?.forEach((order, index) => {
      if (!order?.withDemo) {
        coursePrice += getTariffPriceWithDiscount(
          order?.course?.directionTariffs?.find(
            (tariff) => tariff?.id === order.tariffId,
          ),
          order?.value ?? undefined,
          getDiscount(
            firstFree,
            index,
            extraCourses,
            extraCoursesDiscount ?? [],
            extraCoursesDiscountUnit ?? '',
          ),
        )
      }
    })

    return Number(coursePrice.toFixed(2))
  } catch (error) {
    console.log(error)
    return 0
  }
}

export const getDiscount = (
  isFirstFree: boolean,
  index: number,
  courses: ICourseEnrollmentItem[],
  discounts: number[],
  discountsUnit: string,
): IPromoCodeDiscount | null => {
  try {
    let paidCount = isFirstFree ? -1 : 0

    for (let i = 0; i < index; i++) {
      if (
        courses[i]?.withDemo ||
        courses[i]?.course?.directionTariffs?.find(
          (item) => item?.id === courses[i]?.tariffId,
        )?.type === 'free'
      ) {
        continue
      }
      paidCount++
    }

    if (paidCount < 0) return null

    return {
      type: discountsUnit === 'percent' ? 'percent' : 'sum',
      discount: discounts?.[paidCount] ?? 0,
    } as IPromoCodeDiscount
  } catch (error) {
    return null
  }
}

export const checkIsAllCoursesFree = (
  courses: ICourseEnrollmentItem[],
): boolean => {
  return courses.every(
    (order) =>
      order.withDemo ||
      order.course?.directionTariffs?.find(
        (tariff) => tariff.id === order.tariffId,
      )?.type === 'free',
  )
}
